/**
 * GovShell — Layout institucional da vertical GOV (Gestão Pública).
 *
 * v0.99.9 · Fev/2026 — Waves de replaneamento pós-feedback do Rafael.
 *   Wave 1: sidebar fixa esquerda, header enxuto (logo pequeno + nome do órgão),
 *   footer institucional com LGPD + Transparência + Suporte.
 *
 * NÃO toca em nenhum arquivo do mercado privado — o shell é próprio da
 * vertical GOV. Reutilizamos apenas componentes compartilhados (`api`,
 * `useAuth`), sem alterar rotas ou UI privadas.
 */
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSector } from "@/lib/sectorContext";
import { useAuth } from "@/contexts/AuthContext";
import GovDemoGuide from "./GovDemoGuide";
import GovOnboardingTour from "./GovOnboardingTour";
import {
  ChartLine, BookOpen, LightningSlash, ChatCircleText, UsersThree, Megaphone,
  WhatsappLogo, GearSix, Buildings, ShieldCheck, LifebuoyIcon, ChatCircleDots,
  SignOut, Question, Notebook, Files,
} from "@phosphor-icons/react";

// ── Menu institucional agrupado ──────────────────────────────────
// Ordem seguindo o fluxo do servidor: monitoro, atendo, gerencio,
// comunico e configuro — do mais frequente para o mais raro.
const NAV_GROUPS = [
  {
    label: null, // sem título — item destaque
    items: [
      { to: "/gov/painel", label: "Painel Institucional", icon: ChartLine, testid: "gov-nav-dashboard" },
    ],
  },
  {
    label: "Atendimento",
    items: [
      { to: "/gov/conversas",      label: "Conversas ao vivo",  icon: ChatCircleText, testid: "gov-nav-conversations" },
      { to: "/gov/pendentes",      label: "Perguntas Pendentes", icon: Question,      testid: "gov-nav-pending" },
      { to: "/gov/avaliacoes",     label: "Satisfação Cidadão",  icon: ChatCircleDots, testid: "gov-nav-reviews" },
    ],
  },
  {
    label: "Cadastros",
    items: [
      { to: "/gov/cidadaos",  label: "Cidadãos",   icon: UsersThree, testid: "gov-nav-citizens" },
      { to: "/gov/protocolos", label: "Protocolos", icon: Files,      testid: "gov-nav-protocols" },
    ],
  },
  {
    label: "Comunicação",
    items: [
      { to: "/gov/campanhas",           label: "Comunicados Oficiais", icon: Megaphone, testid: "gov-nav-campaigns" },
      { to: "/gov/modelos",             label: "Modelos Prontos",       icon: LightningSlash, testid: "gov-nav-templates" },
      { to: "/gov/base-conhecimento",   label: "Base de Conhecimento",  icon: BookOpen, testid: "gov-nav-kb" },
    ],
  },
  {
    label: "Configurações",
    items: [
      { to: "/gov/operacao",       label: "Operação do Agente",     icon: LifebuoyIcon, testid: "gov-nav-operation" },
      { to: "/gov/secretarias",    label: "Secretarias & SLA",       icon: Buildings, testid: "gov-nav-departments" },
      { to: "/gov/integracoes",    label: "WhatsApp & Integrações",  icon: WhatsappLogo, testid: "gov-nav-integrations" },
      { to: "/gov/lgpd",           label: "LGPD & Compliance",       icon: ShieldCheck, testid: "gov-nav-lgpd" },
      { to: "/gov/configuracoes",  label: "Ajustes Gerais",          icon: GearSix, testid: "gov-nav-settings" },
    ],
  },
];

export default function GovShell() {
  const { isGov: isGovFromProfile, loading } = useSector();
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [isDemo, setIsDemo] = useState(false);

  // Fonte primária: tenant.sector do auth (sem esperar /sector/me lento em prod)
  const sectorFromAuth = (tenant?.sector || "").toString().toLowerCase();
  const isGovFromAuth = sectorFromAuth.startsWith("gov_");
  const isGov = isGovFromAuth || isGovFromProfile;

  useEffect(() => {
    if (!loading && !isGov && !isGovFromAuth) {
      navigate("/app", { replace: true });
    }
  }, [loading, isGov, isGovFromAuth, navigate]);

  useEffect(() => { setIsDemo(!!tenant?.is_demo_tenant); }, [tenant?.is_demo_tenant]);

  if (loading && !isGovFromAuth) {
    return (
      <div className="min-h-screen grid place-items-center text-emerald-800 bg-slate-50">
        Carregando portal institucional…
      </div>
    );
  }
  if (!isGov) return null;

  const orgName = tenant?.name || "Portal Institucional";
  const orgSubline = (tenant?.address?.city || tenant?.company_trade_name || "");
  const userInitials = ((user?.name || user?.email || "?")
    .split(" ").map(w => w[0]).slice(0, 2).join("")).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="gov-shell">
      {/* ── Header enxuto ────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 backdrop-blur-sm bg-white/95">
        <div className="mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logo-black.png"
              alt="AgentUS"
              className="h-5 w-auto select-none shrink-0"
              draggable="false"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="gov-shell-logo"
            />
            <div className="h-5 w-px bg-slate-200 shrink-0" />
            <div className="min-w-0">
              <div className="font-heading text-[13px] font-semibold text-slate-900 leading-tight truncate" data-testid="gov-shell-org-name">
                {orgName}
                {isDemo && (
                  <span data-testid="gov-header-demo-badge" className="ml-2 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded align-middle">
                    Demo
                  </span>
                )}
              </div>
              {orgSubline && (
                <div className="text-[10.5px] text-slate-500 leading-tight truncate">{orgSubline}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Botão Suporte (sempre visível) */}
            <NavLink
              to="/gov/suporte"
              data-testid="gov-header-support"
              className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-slate-600 hover:text-emerald-800 hover:bg-emerald-50 px-2.5 py-1.5 rounded-md transition-colors"
            >
              <LifebuoyIcon size={14} weight="regular" />
              <span>Suporte</span>
            </NavLink>
            <div className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200">
              <div className="w-6 h-6 rounded-full bg-emerald-700 text-white grid place-items-center text-[10px] font-bold">
                {userInitials}
              </div>
              <div className="text-[11.5px] text-slate-700 leading-tight max-w-[160px] truncate">
                {user?.email}
              </div>
            </div>
            <button
              onClick={() => { logout?.(); navigate("/gov/login", { replace: true }); }}
              data-testid="gov-header-logout"
              className="inline-flex items-center gap-1 text-[12px] text-slate-600 hover:text-rose-700 px-2 py-1"
              title="Sair do portal"
            >
              <SignOut size={13} weight="bold" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Layout: sidebar fixa + conteúdo ─────────────────────── */}
      <div className="flex min-h-[calc(100vh-3.5rem)]">
        {/* Sidebar */}
        <aside
          data-testid="gov-sidebar"
          className="w-60 shrink-0 bg-white border-r border-slate-200 flex flex-col"
        >
          <nav className="flex-1 overflow-y-auto py-4">
            {NAV_GROUPS.map((group, gi) => (
              <div key={gi} className="mb-4">
                {group.label && (
                  <div className="px-4 mb-1.5 text-[9.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                    {group.label}
                  </div>
                )}
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end
                          data-testid={item.testid}
                          className={({ isActive }) => `
                            flex items-center gap-2.5 mx-2 px-3 py-1.5 rounded-md text-[12.5px] transition-colors
                            ${isActive
                              ? "bg-emerald-700 text-white font-medium"
                              : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"}
                          `}
                        >
                          <Icon size={14} weight={"regular"} className="shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
          {/* Rodapé da sidebar — versão + suporte rápido */}
          <div className="border-t border-slate-200 p-3 text-[10.5px] text-slate-400 leading-relaxed">
            <div className="font-semibold text-slate-500 mb-0.5">AgentUS · gov</div>
            <div>Vertical Gestão Pública</div>
            <div>v0.99.9 · Fev/2026</div>
          </div>
        </aside>

        {/* Conteúdo principal */}
        <main className="flex-1 min-w-0 bg-slate-50">
          <div className="max-w-7xl mx-auto p-6">
            <Outlet />
          </div>

          {/* Footer institucional (LGPD + transparência) */}
          <footer className="border-t border-slate-200 bg-white/60 mt-8">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">
                  <ShieldCheck size={11} weight="fill" />
                  LGPD Compliant
                </span>
                <span className="hidden sm:inline">
                  Uso Institucional · dados do órgão pertencem ao próprio ente público
                </span>
              </div>
              <div className="flex items-center gap-4">
                <a href="/legal/termos" target="_blank" rel="noreferrer" className="hover:text-slate-900">Termos</a>
                <NavLink to="/gov/lgpd" className="hover:text-slate-900">Privacidade</NavLink>
                <NavLink to="/gov/suporte" className="hover:text-slate-900">Suporte</NavLink>
              </div>
            </div>
          </footer>
        </main>
      </div>
      {isDemo && <GovDemoGuide />}
    </div>
  );
}
